import { Button,
  Chip,
  Popover,
  styled,
  ToggleButtonGroup,
  ToggleButton, 
  OutlinedInput,
  Box,
  useTheme,
  useFormControl,
  Theme,
  InputBaseProps} from "@mui/material";
import { makeStyles } from "@mui/styles"
import { useSafeSetState } from "ra-core";
import React, { FC, SyntheticEvent, useCallback, useMemo, useRef } from "react";


const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'grid',
  gap: '4px 4px',
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    border: 0,
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
  },
  '& button': {
    padding: `${theme.spacing(0.5)} 0`,
    margin: '0 !important',
    // backgroundColor: 'blue',
    color: theme.palette.text.secondary,
    borderRadius: '6px',
    width: '48px',
    minWidth: '48px',
    '& .MuiTouchRipple-root': {
      minWidth: '48px',
      width: '100%',
    },
  },
}));

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  '&.MuiOutlinedInput-root': {
    borderRadius: '6px',
    height: '32px',
    position: 'absolute',
  },
  '& .MuiOutlinedInput-input': {
    padding: `${theme.spacing(0.75)} 0`,
    fontSize: theme.typography.body2.fontSize,
    width: '48px',
    textAlign: 'center',
  }
}))

const useGridStyles = makeStyles<Theme, { count: number }>((theme) => ({
  root: {
    gridTemplateColumns: (props) => `repeat(${props.count}, 1fr)`
  }
}))

// const useInputStyles = makeStyles<Theme>((theme) => ({
//   root: {
//     borderRadius: '6px',
//     height: '32px',
//     position: 'absolute',
//   },
//   input: {
//     padding: `${theme.spacing(0.75)} 0`,
//     fontSize: theme.typography.body2.fontSize,
//     width: '48px',
//     textAlign: 'center',
//   }
// }))

const defaultChoices: any[] = []

export interface PickableInputProps extends InputBaseProps {
  value?: any;
  choices?: any[];
  choiceRowLength?: number;
  maxRowsCount?: number;
  allowEdit?: boolean;
}

const PickableInput: FC<PickableInputProps> = ({
  value,
  onChange,
  choices,
  choiceRowLength,
  maxRowsCount,
  allowEdit = true,
  ...inputProps
}) => {
  const [anchorEl, setAnchorEl] = useSafeSetState<HTMLDivElement | null>(null);
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>()
  const [inputTop, setInputTop] = useSafeSetState(false)
  const formCtrl = useFormControl()

  const handleClick = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      if (!allowEdit) return;

      setInputTop(true)
      formCtrl?.onFocus()
      inputRef.current?.focus()
    }, theme.transitions.duration.standard)
  }, [theme, inputRef.current, allowEdit, setAnchorEl, setInputTop, formCtrl]);
  
  const handleClose = useCallback(() => {
    if (!formCtrl?.focused) {
      setAnchorEl(null);
      setInputTop(false)
      formCtrl?.onBlur()
    }
  }, [formCtrl, setAnchorEl, setInputTop]);

  const handleValuePicked = useCallback((event: React.MouseEvent<HTMLElement>, pickedValue: any) => {
    if (!inputRef.current) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
    nativeInputValueSetter?.call(inputRef.current, pickedValue);

    const ev2 = new Event('input', { bubbles: true, cancelable: true});
    inputRef.current.dispatchEvent(ev2);

    setAnchorEl(null);
    setInputTop(false)
    formCtrl?.onBlur()
  }, [formCtrl, setAnchorEl, setInputTop, inputRef.current])

  const open = Boolean(anchorEl);

  const finalChoices = choices || defaultChoices
  const finalChoiceRowLength = choiceRowLength || 4
  const finalMaxRowsCount = maxRowsCount || 6

  const isMultPage = finalChoices.length > finalChoiceRowLength * finalMaxRowsCount
  const size = finalChoiceRowLength * finalMaxRowsCount - 2
  const pageCount = Math.ceil((finalChoices.length - 1) / size)
  const [page, setPage] = useSafeSetState(0)
  const gridClasses = useGridStyles({ count: finalChoiceRowLength })
  // const inputClasses = useInputStyles()

  const goNext = useCallback(() => {
    if (page < pageCount) {
      setPage(page + 1)
    }
  }, [page, pageCount, setPage])

  const goPrev = useCallback(() => {
    if (page > 0) {
      setPage(page - 1)
    }
  }, [page, setPage])

  const currPageData = useMemo(() => {
    if (!isMultPage) {
      return finalChoices
    } else {
      const i0 = page === 0 ? 0 : (page * size + 1)
      const len = 
        page === 0 ? size + 1
        : page === pageCount - 1 ? ((finalChoices.length - 1) % size)
        : size
      return [...Array(len).keys()].map(i => finalChoices[i + i0])
    }
  }, [isMultPage, finalChoices, page, size, pageCount])

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignContent: 'baseline',
      }}
    >
      <Chip 
        role="button"
        variant="outlined"
        label={value+''} 
        onClick={handleClick}
        sx={{
          borderRadius: '6px',
          width: 48,
          height: 32,
        }}
        style={{
          opacity: inputTop ? 0 :  1,
          zIndex: inputTop ? -1 : 1,
        }}
        aria-labelledby={!!inputProps && inputProps["aria-labelledby"]}
        aria-describedby={!!inputProps && inputProps["aria-describedby"]}
      />
      <StyledOutlinedInput
        size="small" 
        // className={inputClasses.root}
        value={value}
        onChange={onChange}
        style={{
          opacity: inputTop ? 1 :  0,
          zIndex: inputTop ? theme.zIndex.modal + 1 : 0
        }}
        tabIndex={inputTop ? 0 : -1}
        inputRef={inputRef}
        // inputProps={{
        //   className: inputClasses.input,
        // }}
        {...inputProps}
        inputProps={{
          ...inputProps.inputProps,
          tabIndex: inputTop ? 0 : -1,
          'aria-hidden': true
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: '6px',
            p: 0.5,
          }
        }}
      >
        <StyledToggleButtonGroup
          value={value}
          onChange={handleValuePicked}
          exclusive
          className={gridClasses.root}
        >
          {isMultPage && page > 0 ?
            <Button color="inherit" onClick={goPrev}>...</Button> : null}

          {currPageData.map(d => (
            <ToggleButton key={d.toString()}
              value={d.toString()}
            >
              {d}
            </ToggleButton>
          ))}

          {isMultPage && page < pageCount - 1 ?
            <Button color="inherit" onClick={goNext}>...</Button> : null}

        </StyledToggleButtonGroup>
      </Popover>
    </Box>
  )
}

export default PickableInput;
